import React, { useState, useEffect } from "react";
import { 
  Table, 
  Card, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Select,
  Modal,
  message,
  Dropdown,
  Menu,
  Row,
  Col,
  Spin
} from "antd";
import { 
  SearchOutlined, 
  PlusOutlined, 
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { collection, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { firestore } from "../../firebase";
import { useAuth } from "../Context";
import { useNotification } from "../NotificationContext";
import FiltroSucursal from "../sucursales/FiltroSucursal";
import AgregarEquipoForm from "./AgregarEquipoForm";
import Title from "antd/es/typography/Title";

const { Search } = Input;
const { Option } = Select;

const BuscarEquipo = () => {
  const [equipos, setEquipos] = useState([]);
  const [equiposFiltrados, setEquiposFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [sucursalFiltro, setSucursalFiltro] = useState("");
  
  // Modales
  const [modalAgregar, setModalAgregar] = useState(false);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);

  const { sucursal, isAdmin, token } = useAuth();
  const openNotificationWithIcon = useNotification();

  useEffect(() => {
    setSucursalFiltro(sucursal || "");
  }, [sucursal]);

  useEffect(() => {
    if (!sucursalFiltro) return;

    setLoading(true);
    const q = collection(firestore, `sucursal/${sucursalFiltro}/equipo`);
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const equiposData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEquipos(equiposData);
      setEquiposFiltrados(equiposData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [sucursalFiltro]);

  // Filtros
  useEffect(() => {
    let filtered = equipos;

    if (searchText) {
      filtered = filtered.filter(equipo =>
        equipo.nombre?.toLowerCase().includes(searchText.toLowerCase()) ||
        equipo.marca?.toLowerCase().includes(searchText.toLowerCase()) ||
        equipo.modelo?.toLowerCase().includes(searchText.toLowerCase()) ||
        equipo.numeroSerie?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filtroTipo) {
      filtered = filtered.filter(equipo => equipo.tipo === filtroTipo);
    }

    if (filtroEstado) {
      filtered = filtered.filter(equipo => equipo.estado === filtroEstado);
    }

    setEquiposFiltrados(filtered);
  }, [equipos, searchText, filtroTipo, filtroEstado]);

  const handleEliminar = async (equipo) => {
    Modal.confirm({
      title: "¿Eliminar equipo?",
      content: `¿Está seguro que desea eliminar el equipo "${equipo.nombre}"?`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteDoc(doc(firestore, `sucursal/${sucursalFiltro}/equipo/${equipo.id}`));
          message.success("Equipo eliminado correctamente");
          openNotificationWithIcon("success", "Éxito", "El equipo se eliminó correctamente");
        } catch (error) {
          console.error("Error al eliminar equipo:", error);
          message.error("Error al eliminar el equipo");
          openNotificationWithIcon("error", "Error", "Hubo un problema al eliminar el equipo");
        }
      }
    });
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "operativo": return "green";
      case "mantenimiento": return "orange";
      case "reparacion": return "red";
      case "fuera_servicio": return "gray";
      default: return "default";
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case "operativo": return "Operativo";
      case "mantenimiento": return "Mantenimiento";
      case "reparacion": return "Reparación";
      case "fuera_servicio": return "Fuera de Servicio";
      default: return estado;
    }
  };

  const menuAcciones = (record) => (
    <Menu>
      <Menu.Item 
        key="editar" 
        icon={<EditOutlined />}
        onClick={() => {
          setEquipoSeleccionado(record);
          setModalAgregar(true);
        }}
      >
        Editar
      </Menu.Item>
      {isAdmin && (
        <Menu.Item 
          key="eliminar" 
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleEliminar(record)}
        >
          Eliminar
        </Menu.Item>
      )}
    </Menu>
  );

  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => a.nombre?.localeCompare(b.nombre),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      render: (tipo) => <Tag color="blue">{tipo}</Tag>,
    },
    {
      title: "Marca/Modelo",
      key: "marca_modelo",
      render: (_, record) => (
        <div>
          <div><strong>{record.marca}</strong></div>
          <div style={{ color: "#666" }}>{record.modelo}</div>
        </div>
      ),
    },
    {
      title: "Número de Serie",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {getEstadoTexto(estado)}
        </Tag>
      ),
    },
    {
      title: "Ubicación",
      dataIndex: "ubicacion",
      key: "ubicacion",
    },
    {
      title: "Fecha de Compra",
      dataIndex: "fechaCompra",
      key: "fechaCompra",
      render: (fecha) => fecha ? new Date(fecha.toDate()).toLocaleDateString() : "N/A",
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (_, record) => (
        <Dropdown overlay={menuAcciones(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const tiposEquipo = [...new Set(equipos.map(e => e.tipo).filter(Boolean))];

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3}>Gestión de Equipos</Title>

      {/* Filtros y controles */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Row gutter={16} align="middle">
            <Col xs={24} md={8}>
              <Search
                placeholder="Buscar por nombre, marca, modelo o número de serie"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            
            <Col xs={12} md={4}>
              <Select
                placeholder="Filtrar por tipo"
                value={filtroTipo}
                onChange={setFiltroTipo}
                allowClear
                style={{ width: "100%" }}
              >
                {tiposEquipo.map(tipo => (
                  <Option key={tipo} value={tipo}>{tipo}</Option>
                ))}
              </Select>
            </Col>

            <Col xs={12} md={4}>
              <Select
                placeholder="Filtrar por estado"
                value={filtroEstado}
                onChange={setFiltroEstado}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value="operativo">Operativo</Option>
                <Option value="mantenimiento">Mantenimiento</Option>
                <Option value="reparacion">Reparación</Option>
                <Option value="fuera_servicio">Fuera de Servicio</Option>
              </Select>
            </Col>

            {isAdmin && (
              <Col xs={24} md={4}>
                <FiltroSucursal 
                  onSucursalChange={setSucursalFiltro} 
                  value={sucursalFiltro} 
                />
              </Col>
            )}

            <Col xs={24} md={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setModalAgregar(true)}
                style={{ width: "100%" }}
              >
                Agregar Equipo
              </Button>
            </Col>
          </Row>

          <div style={{ color: "#666" }}>
            Total: {equiposFiltrados.length} equipos
          </div>
        </Space>
      </Card>

      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={equiposFiltrados}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} de ${total} equipos`,
        }}
        scroll={{ x: 1000 }}
      />

      {/* Modal de Agregar/Editar */}
      <Modal
        title={equipoSeleccionado ? "Editar Equipo" : "Agregar Equipo"}
        open={modalAgregar}
        onCancel={() => {
          setModalAgregar(false);
          setEquipoSeleccionado(null);
        }}
        footer={null}
        width={800}
      >
        <AgregarEquipoForm
          equipo={equipoSeleccionado}
          sucursal={sucursalFiltro}
          onClose={() => {
            setModalAgregar(false);
            setEquipoSeleccionado(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default BuscarEquipo;
